/**
 * Apartsales — Лента / Каталог (Tab 2).
 *
 * Показывает РАСПАРСЕННЫЙ инвентарь, сгруппированный ПО ПРОЕКТАМ (ЖК).
 *
 * Источник: fetchUnitCards({ status: 'pending_review' }) — это ровно
 * распознанные из открытых источников объекты, ещё не верифицированные
 * (без поквартирного реестра/фото/AS-Score; ждут ЕИСЖС + Apartsales Studio).
 * Фильтр server-side по статусу, поэтому демо/боевые юниты сюда НЕ попадают
 * и ничего не «тонет» под лимитом сортировки.
 *
 * Каждая карточка = ЖК (застройщик, город, цена «от», число планировок,
 * срок, плашка «Черновик»). Тап → детальная репрезентативного юнита.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { SearchBar } from '~/components/SearchBar';
import { StoriesRail } from '~/components/StoriesRail';
import { fetchUnitCards } from '~/lib/unit-card-api';
import { buildSlugUrl } from '~/lib/slug-routing';
import { formatPriceCop, type UnitCard } from '@apartsales/shared';

export const Route = createFileRoute('/feed')({
  component: FeedScreen,
});

type ProjectGroup = {
  projectId: string;
  name: string;
  developer: string;
  city: string;
  district: string | null;
  completion: string | null;
  minPriceCop: number;
  unitCount: number;
  rep: UnitCard;
};

function plural(n: number, forms: [string, string, string]): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return forms[0];
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return forms[1];
  return forms[2];
}

function completionLabel(date: string | null | undefined): string | null {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `сдача Q${q} ${String(d.getFullYear()).slice(2)}`;
}

function groupByProject(units: UnitCard[]): ProjectGroup[] {
  const map = new Map<string, ProjectGroup>();
  for (const u of units) {
    const g = map.get(u.project_id);
    if (!g) {
      map.set(u.project_id, {
        projectId: u.project_id,
        name: u.project.name,
        developer: u.project.developer.brand_name,
        city: u.project.city,
        district: u.project.district ?? null,
        completion: u.project.completion_date ?? null,
        minPriceCop: u.price,
        unitCount: 1,
        rep: u,
      });
    } else {
      g.unitCount += 1;
      if (u.price < g.minPriceCop) {
        g.minPriceCop = u.price;
        g.rep = u;
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

function FeedScreen() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['feed-parsed-projects'],
    queryFn: () => fetchUnitCards({ status: 'pending_review', limit: 100 }),
    staleTime: 60_000,
  });

  const groups = data ? groupByProject(data) : [];

  return (
    <div className="px-4" style={{ color: 'var(--color-label, #1c1c1e)' }}>
      <SearchBar />

      <div className="-mx-4 mt-1">
        <StoriesRail />
      </div>

      {isLoading ? (
        <FeedSkeleton />
      ) : isError ? (
        <FeedError onRetry={() => refetch()} />
      ) : groups.length > 0 ? (
        <ProjectList groups={groups} unitCount={data?.length ?? 0} />
      ) : (
        <FeedEmptyState />
      )}
    </div>
  );
}

function ProjectList({ groups, unitCount }: { groups: ProjectGroup[]; unitCount: number }) {
  return (
    <section className="mt-3 pb-6">
      <div className="mb-1 flex items-baseline justify-between px-1">
        <h2
          className="text-[17px] font-bold tracking-[-0.2px]"
          style={{ fontFamily: 'var(--font-display)', color: 'var(--color-label, #1c1c1e)' }}
        >
          Распарсенные проекты
        </h2>
        <span className="text-[12px]" style={{ color: 'var(--color-label-2, #8a8a8e)' }}>
          {groups.length} {plural(groups.length, ['ЖК', 'ЖК', 'ЖК'])} · {unitCount} {plural(unitCount, ['объект', 'объекта', 'объектов'])}
        </span>
      </div>

      <ul className="mt-3 flex flex-col gap-2.5">
        {groups.map((g) => (
          <ProjectCard key={g.projectId} group={g} />
        ))}
      </ul>
    </section>
  );
}

function ProjectCard({ group }: { group: ProjectGroup }) {
  const navigate = useNavigate();
  const district = group.district ? ` · ${group.district}` : '';
  const completion = completionLabel(group.completion);

  const go = () => {
    try {
      navigate({ to: buildSlugUrl(group.rep) });
    } catch {
      /* slug build defensive no-op */
    }
  };

  return (
    <li>
      <button
        type="button"
        onClick={go}
        className="w-full rounded-2xl p-3.5 text-left transition-transform active:scale-[0.985]"
        style={{ background: '#ffffff', boxShadow: 'var(--shadow-card, 0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04))' }}
      >
        <div className="min-w-0">
          <p
            className="truncate text-[11px] font-semibold uppercase tracking-[0.4px]"
            style={{ color: 'var(--color-label-2, #8a8a8e)' }}
          >
            {group.developer}
          </p>
          <h3
            className="truncate text-[16px] font-bold tracking-[-0.2px]"
            style={{ color: 'var(--color-label, #1c1c1e)' }}
          >
            {group.name}
          </h3>
          <p className="truncate text-[13px]" style={{ color: 'var(--color-label-2, #8a8a8e)' }}>
            {group.city}{district}
          </p>
        </div>

        <div className="mt-2.5 flex items-end justify-between gap-2">
          <div>
            <p
              className="text-[18px] font-extrabold tracking-[-0.4px]"
              style={{ color: 'var(--color-label, #1c1c1e)' }}
            >
              от {formatPriceCop(group.minPriceCop)}
            </p>
            {completion && (
              <p className="text-[12px]" style={{ color: 'var(--color-label-2, #8a8a8e)' }}>
                {completion}
              </p>
            )}
          </div>
          <span
            className="rounded-full px-2.5 py-1 text-[12px] font-medium"
            style={{ background: 'rgba(120,120,128,0.10)', color: 'var(--color-label-2, #8a8a8e)' }}
          >
            {group.unitCount} {plural(group.unitCount, ['планировка', 'планировки', 'планировок'])}
          </span>
        </div>
      </button>
    </li>
  );
}

function FeedSkeleton() {
  return (
    <div className="mt-3 flex flex-col gap-2.5">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-[108px] w-full animate-pulse rounded-2xl"
          style={{ background: '#f1f1f3' }}
        />
      ))}
    </div>
  );
}

function FeedError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-20 text-center">
      <h2 className="text-[18px] font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--color-label, #1c1c1e)' }}>
        Не удалось загрузить каталог
      </h2>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-full px-4 py-2 text-[14px] font-semibold text-white"
        style={{ background: 'var(--color-brand-blue, #2A96FB)' }}
      >
        Повторить
      </button>
    </div>
  );
}

function FeedEmptyState() {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-20 text-center">
      <div
        className="grid h-16 w-16 place-items-center rounded-full"
        style={{ background: 'var(--color-glass-thin, rgba(120,120,128,0.12))' }}
        aria-hidden
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <path d="M4 6h16M4 12h16M4 18h10" stroke="var(--color-label-2, #8a8a8e)" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </div>
      <h2
        className="text-[20px] font-extrabold tracking-[-0.3px]"
        style={{ fontFamily: 'var(--font-display)', color: 'var(--color-label, #1c1c1e)' }}
      >
        Пока пусто
      </h2>
      <p className="max-w-[300px] text-[14px] leading-relaxed" style={{ color: 'var(--color-label-2, #8a8a8e)' }}>
        Распарсенные проекты появятся здесь после первого пакета объектов.
      </p>
    </div>
  );
}
